// src/pages/Reports/IssuanceReporting/IssuanceStock/IssuanceSummaryTab.tsx
// RPT-001 (README.md mục 7) — tổng hợp phát hành vé, 1 dòng = 1 (Kế hoạch, Ký hiệu).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getIssuanceSummaryReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { cleanParams, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, IssuanceSummaryReportRow, ReportPaginationState } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATUS_OPTIONS = [
  { value: 1, label: st('da_tao_label') },
  { value: 2, label: st('da_duyet_label') },
  { value: 3, label: st('da_sinh_qr_label') },
  { value: 4, label: st('da_gui_in_label') },
  { value: 5, label: st('da_nhan_label') },
  { value: 6, label: st('da_huy_label_2') },
];

const IssuanceSummaryTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<IssuanceSummaryReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({
      IssuancePlanId: planId || undefined,
      Symbol: symbol || undefined,
      Status: status || undefined,
    }),
    [planId, symbol, status],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIssuanceSummaryReportApi(
        cleanParams({
          ...buildFilterParams(),
          PageIndex: pagination.currentPage,
          PageSize: pagination.pageSize,
        }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_tong_message_2'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, symbol, status]);

  const handleFilterChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/IssuanceSummary`, buildFilterParams(), format, 'TongHopPhatHanh');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); setPagination((p) => ({ ...p, currentPage: 1 })); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('ky_hieu_label')}</Label>
          <Input type="text" placeholder={st('vd_n_b_placeholder')} value={symbol} onChange={handleFilterChange(setSymbol)} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('trang_thai_ky_label')}</Label>
          <Input type="select" value={status} onChange={handleFilterChange(setStatus)}>
            <option value="">{st('tat_ca_trang_thai_option')}</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Input>
        </Col>
        <Col md={3} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('loai_ve_column')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th className="text-end">{st('tong_sl_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th className="text-end">{st('da_in_column')}</th>
                <th className="text-end">{st('da_nhap_kho_column')}</th>
                <th className="text-end">{st('da_giao_column')}</th>
                <th className="text-end">{st('con_ton_column')}</th>
                <th className="text-end">{st('thu_hoi_column')}</th>
                <th className="text-end">{st('huy_column')}</th>
                <th className="text-end">{st('chenh_lech_column')}</th>
                <th>{st('trang_thai_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={14} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.issuancePlanId}-${row.symbol}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>{row.ticketTypeName || row.ticketType}</td>
                  <td>{row.symbol}</td>
                  <td className="text-end">{formatQty(row.totalQuantity)}</td>
                  <td className="text-end">{formatCurrency(row.totalValue)}</td>
                  <td className="text-end">{formatQty(row.printedQuantity)}</td>
                  <td className="text-end">{formatQty(row.warehousedQuantity)}</td>
                  <td className="text-end">{formatQty(row.deliveredQuantity)}</td>
                  <td className="text-end">{formatQty(row.remainingQuantity)}</td>
                  <td className="text-end">{formatQty(row.recalledQuantity)}</td>
                  <td className="text-end">{formatQty(row.cancelledQuantity)}</td>
                  <td className="text-end">{formatQty(row.discrepancyQuantity)}</td>
                  <td><span className="badge bg-info-subtle text-info">{row.statusName}</span></td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default IssuanceSummaryTab;
